import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { Shield, Lock, AlertTriangle, Eye } from "lucide-react";

export const WalletSecurity = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Wallet Security</h1>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Best practices for keeping your Solana wallet and funds secure</p>
        </div>

        {/* Seed Phrase Security */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Protecting Your Seed Phrase</h2>
          
          <p className="text-muted-foreground mb-6">
            Your seed phrase (also called recovery phrase or mnemonic) is the most important piece of 
            information for your wallet. Anyone with your seed phrase has complete access to your funds.
          </p>

          <div className="space-y-4 mb-6">
            <div className="p-6 border border-emerald-500/20 rounded-lg bg-emerald-500/5">
              <div className="flex gap-3">
                <Shield className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-2">DO: Secure Storage</h3>
                  <ul className="text-sm text-muted-foreground space-y-1 pl-4 list-disc">
                    <li>Write it down on paper and store in a safe location</li>
                    <li>Use a hardware wallet for large amounts</li>
                    <li>Consider metal backup solutions for fire/water resistance</li>
                    <li>Store in multiple secure locations (don't put all backups in one place)</li>
                    <li>Use a password manager with strong encryption</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="p-6 border border-red-500/20 rounded-lg bg-red-500/5">
              <div className="flex gap-3">
                <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-2">DON'T: Dangerous Practices</h3>
                  <ul className="text-sm text-muted-foreground space-y-1 pl-4 list-disc">
                    <li>Never share your seed phrase with anyone (including "support")</li>
                    <li>Never store it in email, cloud storage, or screenshots</li>
                    <li>Never enter it on websites or apps (except official wallet recovery)</li>
                    <li>Never send it via messaging apps or social media</li>
                    <li>Never type it on a computer that might have keyloggers</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Browser Extension Security */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Browser Extension Safety</h2>
          
          <div className="space-y-4">
            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Lock className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">Download from Official Sources</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                Only install wallet extensions from official sources:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 pl-4 list-disc">
                <li>Chrome Web Store or official browser extension stores</li>
                <li>Official wallet websites (phantom.app, solflare.com, backpack.app)</li>
                <li>Verify the developer/publisher name matches the official wallet</li>
              </ul>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <Eye className="h-6 w-6 text-amber-500" />
                </div>
                <h3 className="font-semibold text-lg">Verify Permissions</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                When connecting your wallet to websites, always verify what you're approving. 
                Check the transaction details before signing, and never blindly approve transactions.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold mb-2">Lock Your Wallet</h3>
              <p className="text-sm text-muted-foreground">
                Always lock your wallet when not in use. Most wallet extensions have an auto-lock feature - 
                enable it and set a reasonable timeout period.
              </p>
            </div>
          </div>
        </section>

        {/* Transaction Security */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Safe Transaction Practices</h2>
          
          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Always Verify Before Signing</h3>
              <p className="text-sm text-muted-foreground">
                Before approving any transaction in your wallet, carefully review:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 pl-4 list-disc mt-2">
                <li>The website you're interacting with (check the URL)</li>
                <li>The amount being sent or approved</li>
                <li>The recipient address</li>
                <li>The transaction fee</li>
              </ul>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Start with Small Test Transactions</h3>
              <p className="text-sm text-muted-foreground">
                When sending funds to a new address or using a new protocol, start with a small test 
                transaction to verify everything works correctly before sending larger amounts.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Double-Check Addresses</h3>
              <p className="text-sm text-muted-foreground">
                Always verify the full address when sending funds. Malware can replace copied addresses 
                with attacker addresses. Check both the beginning and end of the address.
              </p>
            </div>
          </div>
        </section>

        {/* Phishing Protection */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Avoiding Phishing Attacks</h2>
          
          <div className="space-y-4">
            <div className="p-6 border border-red-500/20 rounded-lg bg-red-500/5">
              <div className="flex gap-3">
                <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold mb-2">Common Phishing Tactics</h3>
                  <ul className="text-sm text-muted-foreground space-y-2 pl-4 list-disc">
                    <li>Fake websites with similar URLs (e.g., nordex-trade.com instead of nordex.trade)</li>
                    <li>Impersonators claiming to be support staff asking for your seed phrase</li>
                    <li>Emails or messages with urgent warnings about your account</li>
                    <li>Social media DMs offering "help" or "exclusive opportunities"</li>
                    <li>Fake token airdrops that request wallet approval</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">How to Stay Safe</h3>
              <ul className="text-sm text-muted-foreground space-y-2 pl-4 list-disc">
                <li>Bookmark official websites and always use bookmarks</li>
                <li>Check the URL carefully before connecting your wallet</li>
                <li>Enable HTTPS-only browsing</li>
                <li>Be skeptical of unsolicited messages</li>
                <li>Never click links in unexpected emails or DMs</li>
                <li>Verify social media accounts (check for verification badges)</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Hardware Wallets */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Hardware Wallets</h2>
          
          <p className="text-muted-foreground mb-6">
            For maximum security, especially for large amounts, consider using a hardware wallet like 
            Ledger. Hardware wallets store your private keys offline, making them immune to online attacks.
          </p>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-emerald-500 mb-2">✓ Advantages</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Private keys never leave the device</li>
                <li>• Immune to computer viruses</li>
                <li>• Requires physical confirmation for transactions</li>
                <li>• Secure even on compromised computers</li>
              </ul>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-amber-500 mb-2">⚠ Considerations</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Requires purchasing hardware ($50-200)</li>
                <li>• Less convenient for frequent trading</li>
                <li>• Physical device can be lost or damaged</li>
                <li>• Still need to secure seed phrase backup</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Multi-Wallet Strategy */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Multi-Wallet Strategy</h2>
          
          <p className="text-muted-foreground mb-6">
            Consider using multiple wallets for different purposes:
          </p>

          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">1. Cold Storage Wallet</h3>
              <p className="text-sm text-muted-foreground">
                Hardware wallet or paper wallet for long-term holdings. Never connected to dapps or trading platforms.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">2. Hot Wallet for Trading</h3>
              <p className="text-sm text-muted-foreground">
                Browser extension wallet for active trading on Nordex and other platforms. Keep only what 
                you need for trading here.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">3. Experimental Wallet</h3>
              <p className="text-sm text-muted-foreground">
                Separate wallet with minimal funds for trying new protocols or testing suspicious dapps.
              </p>
            </div>
          </div>
        </section>

        {/* Security Checklist */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Security Checklist</h2>
          
          <div className="space-y-2">
            <div className="p-3 bg-muted/30 rounded-lg text-sm flex items-start gap-3">
              <input type="checkbox" className="mt-1" disabled />
              <span>Seed phrase written down and stored securely offline</span>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg text-sm flex items-start gap-3">
              <input type="checkbox" className="mt-1" disabled />
              <span>Wallet extension downloaded from official source</span>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg text-sm flex items-start gap-3">
              <input type="checkbox" className="mt-1" disabled />
              <span>Strong password set for wallet</span>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg text-sm flex items-start gap-3">
              <input type="checkbox" className="mt-1" disabled />
              <span>Auto-lock enabled on wallet</span>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg text-sm flex items-start gap-3">
              <input type="checkbox" className="mt-1" disabled />
              <span>Never shared seed phrase with anyone</span>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg text-sm flex items-start gap-3">
              <input type="checkbox" className="mt-1" disabled />
              <span>Always verify transaction details before signing</span>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg text-sm flex items-start gap-3">
              <input type="checkbox" className="mt-1" disabled />
              <span>Using bookmarks for frequently visited sites</span>
            </div>
            <div className="p-3 bg-muted/30 rounded-lg text-sm flex items-start gap-3">
              <input type="checkbox" className="mt-1" disabled />
              <span>Computer and browser kept up to date</span>
            </div>
          </div>
        </section>

        <DocsNavigation 
          previous={{ title: "Performance Lab", href: "/performance-lab" }}
          next={{ title: "Understanding Risks", href: "/understanding-risks" }}
        />
      </div>
    </DocsLayout>
  );
};

